import { CircularProgress } from '@components/common/Icons';
import { CvDetailInterface } from '@components/templates/cv/interface/CvDetailInterface';
import { HandThumbDownIcon, HandThumbUpIcon, FlagIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { Button } from '@components/common';

/**
 * @description Component to render the comments section.
 * @param cvDetail - the cv detail object
 * @param onReportEvaluation - callback to open the report evaluation modal
 */
export const EvaluateBia = ({
  cvDetail,
  onReportEvaluation,
}: CvDetailInterface & { onReportEvaluation?: () => void }) => {
  // Translation
  const { t } = useTranslation('common');

  const renderIconByScore = (score: number) => {
    if (score >= 0 && score <= 50) {
      return <HandThumbDownIcon className="size-5 min-w-5 text-error" />;
    }
    if (score > 50 && score <= 69) {
      return <HandThumbUpIcon className="size-5 min-w-5 text-warning" />;
    }
    return <HandThumbUpIcon className="size-5 min-w-5 text-secondary" />;
  };

  const backgroundByScore = (score: number) => {
    if (score >= 0 && score <= 50) {
      return 'bg-error/5';
    }
    if (score > 50 && score <= 69) {
      return 'bg-warning/5';
    }
    return 'bg-secondary/5';
  };

  // Get evaluation from either application.evaluation or root level evaluation
  const evaluation = cvDetail?.application?.evaluation || cvDetail?.evaluation;

  const score = evaluation?.score ?? 0;
  const roundedScore = Math.ceil(score);

  // Render component
  // ******************************************************
  return (
    <>
      {/* Added bia evaluation on cv detail, if exist */}
      {
        evaluation && (
          <div id="evaluateBia">
            <div className="flex items-center justify-between py-3 text-sm">
              <div className="flex w-full items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="font-bold text-ink">{t('bia.evaluate.text')}</div>
                  {onReportEvaluation && cvDetail?.application?.applicantId && (
                    <Button
                      styling="text"
                      color="ink"
                      className="!text-xs !text-ink underline underline-offset-2 hover:!text-ink hover:!no-underline"
                      onClick={onReportEvaluation}
                    >
                      <FlagIcon className="mr-1 size-4 text-error" />
                      {t('cv.action.report.evaluation')}
                    </Button>
                  )}
                  {/* <CustomBiaEvaluate className="size-8" /> */}
                </div>
                {/* Circular Progress here if score > 0 */}
                {roundedScore > 0 && (
                  <CircularProgress score={score} size={48} />
                )}
              </div>
            </div>

            <div className="rounded-md bg-surface p-2">
              <div className={`${backgroundByScore(score ?? 0)} mb-4 rounded-md p-2`}>
                <div className="flex items-start gap-2 rounded-md p-2">
                  {renderIconByScore(score ?? 0)}
                  <div className="text-sm font-thin">
                    {evaluation?.summary}
                  </div>
                </div>
              </div>

              <div className="mb-3 mt-1 text-sm">
                <p className="font-bold">
                  {`${t('bia.evaluate.overall.score')} `}
                  <span className="font-normal">
                    {Math.ceil(score ?? 0)}
                    /100
                  </span>
                </p>
              </div>
              <div className="mb-3 text-sm">
                <p className="font-bold">{`${t('bia.evaluate.scores')} `}</p>
                <ul className="list-disc pl-5">
                  <li>
                    <p className="text-xs font-bold">
                      {`${t('bia.evaluate.scores.skills')} `}
                      <span className="font-normal">
                        {Math.ceil(evaluation?.skillsMatchScore ?? 0)}
                        /100
                      </span>
                    </p>
                    <p className="text-xs">
                      {evaluation?.skillsMatchExplanation}
                    </p>
                  </li>
                  {evaluation?.experienceMatchExplanation && (
                  <li>
                    <p className="text-xs font-bold">
                      {`${t('bia.evaluate.scores.experience')} `}
                      <span className="font-normal">
                        {Math.ceil(evaluation?.experienceMatchScore ?? 0)}
                        /100
                      </span>
                    </p>
                    <p className="text-xs">
                      {evaluation?.experienceMatchExplanation}
                    </p>
                  </li>
                  )}
                  {evaluation?.educationMatchExplanation && (
                  <li>
                    <p className="text-xs font-bold">
                      {`${t('bia.evaluate.scores.education')} `}
                      <span className="font-normal">
                        {Math.ceil(evaluation?.educationMatchScore ?? 0)}
                        /100
                      </span>
                    </p>
                    <p className="text-xs">
                      {evaluation?.educationMatchExplanation}
                    </p>
                  </li>
                  )}
                  <li>
                    <p className="text-xs font-bold">
                      {`${t('bia.evaluate.scores.location')} `}
                      <span className="font-normal">
                        {Math.ceil(evaluation?.locationMatchScore ?? 0)}
                        /100
                      </span>
                    </p>
                    <p className="text-xs">
                      {evaluation?.locationMatchExplanation}
                    </p>
                  </li>
                </ul>

              </div>
              <div className="mb-3 text-sm">
                <p className="font-bold text-secondary">
                  {t('bia.evaluate.strengths')}
                </p>
                <p className="whitespace-pre-line">
                  {evaluation?.strengths}
                </p>
              </div>
              <div className="text-sm">
                <p className="font-bold text-error">{t('bia.evaluate.weaknesses')}</p>
                <p className="whitespace-pre-line">
                  {evaluation?.weaknesses}
                </p>
              </div>
            </div>
          </div>
        )
      }
    </>
  );
};
